import { useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';

// project imports
import Search from './Search';
import Notification from './Notification';
import MobileSection from './MobileSection';
import { useAuth } from 'contexts/AuthContext';
import { formatUserBadgeName } from 'utils/userDisplayName';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import RollbackOutlined from '@ant-design/icons/RollbackOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from 'assets/images/users/logovrp1.png';

const HEADER_RETURN_HISTORY_KEY = 'header_return_history';

const getFullPath = (location) => `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const downSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fullPath = getFullPath(location);

  useEffect(() => {
    try {
      const rawHistory = sessionStorage.getItem(HEADER_RETURN_HISTORY_KEY);
      const history = rawHistory ? JSON.parse(rawHistory) : {};
      const current = String(history.current || '');

      if (current !== fullPath) {
        sessionStorage.setItem(
          HEADER_RETURN_HISTORY_KEY,
          JSON.stringify({
            previous: current || '',
            current: fullPath
          })
        );
      }
    } catch {}
  }, [fullPath]);

  const handleLogout = () => {
    logout();
  };

  const goAreaSelector = () => {
    const pathname = location?.pathname || '';
    const cuadrosObraMatch = pathname.match(/^\/cuadros\/obras\/([^/]+)(?:\/.*)?$/);
    const familyDetailMatch = pathname.match(/^\/familias\/([^/]+)\/gestion-obras\/([^/]+)$/);
    const familyListMatch = pathname.match(/^\/familias\/([^/]+)\/gestion-obras$/);
    const familyProjectsMatch = pathname.match(/^\/familias\/([^/]+)\/gestion-proyectos$/);
    const canGoBack = typeof window !== 'undefined' && Number(window.history?.state?.idx) > 0;
    let storedPrevious = '';

    try {
      const rawHistory = sessionStorage.getItem(HEADER_RETURN_HISTORY_KEY);
      const history = rawHistory ? JSON.parse(rawHistory) : {};
      storedPrevious = String(history.previous || '');
    } catch {}

    if (familyProjectsMatch) {
      navigate('/areas/gestion');
      return;
    }

    if (cuadrosObraMatch) {
      if (canGoBack) {
        navigate(-1);
        return;
      }
      navigate('/cuadros/dashboard/default');
      return;
    }

    if (familyListMatch) {
      navigate('/areas/gestion');
      return;
    }

    if (familyDetailMatch) {
      const familySlug = familyDetailMatch[1];
      if (canGoBack) {
        navigate(-1);
        return;
      }
      navigate(`/familias/${familySlug}/gestion-obras`);
      return;
    }

    if (canGoBack) {
      navigate(-1);
      return;
    }

    if (storedPrevious) {
      navigate(storedPrevious);
      return;
    }

    navigate('/areas/gestion');
  };
  const goButtonTooltip = 'Volver';
  const ROLE_COLOR_MAP = {
    admin: '#ff5a5a',
    supervisor: '#f5c16c',
    comercial: '#166534',
    operario: '#0083c6',
    contabilidad: '#2ecc71'
  };

  const ROLE_LABEL_MAP = {
    admin: 'Admin',
    supervisor: 'Supervisor',
    comercial: 'Comercial',
    operario: 'Operario',
    contabilidad: 'Contabilidad'
  };

  const getRoleBackgroundColor = (role) => ROLE_COLOR_MAP[role] || '#6b7280';
  const getRoleText = (role) => ROLE_LABEL_MAP[role] || role || 'Sin rol';
  const userDisplayName = formatUserBadgeName(user);
  return (
    <>
      {!downLG && <Search />}
      {downLG && <Box sx={{ width: '90%', ml: 1 }} />}

      {/* Logo + Perfil (nombre, rol) */}
      <Stack direction="row" alignItems="center" spacing={3} sx={{ mr: 3 }}>
        <Avatar
          alt="VRP Logo"
          src={logoImage}
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'primary.light',
            '& img': {
              objectFit: 'contain',
              padding: '2px'
            }
          }}
        />
        <Stack direction="column" sx={{ alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', lineHeight: 1.2, fontWeight: 600 }}>
            {userDisplayName}
          </Typography>
          <Box
            sx={{
              bgcolor: getRoleBackgroundColor(user?.role),
              color: '#fff',
              px: 2.5,
              py: 0.35,
              borderRadius: 999,
              fontSize: '0.75rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              minWidth: 110,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)'
            }}
          >
            {getRoleText(user?.role)}
          </Box>
        </Stack>
      </Stack>

      {/* Notificaciones + volver */}
      <Box sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Notification sx={{ fontSize: '1.1rem' }} />
        <Tooltip title={goButtonTooltip}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={goAreaSelector}
            startIcon={<RollbackOutlined />}
            sx={{
              ml: 1,
              minWidth: downSM ? 36 : undefined,
              px: downSM ? 1 : undefined,
              '& .MuiButton-startIcon': {
                mr: downSM ? 0 : 1
              }
            }}
          >
            {downSM ? null : 'Volver'}
          </Button>
        </Tooltip>
      </Box>

      {/* BotÇün Salir */}
      {!downSM && (
        <Button variant="outlined" color="error" size="small" onClick={handleLogout} startIcon={<LogoutOutlined />} sx={{ ml: 2 }}>
          Salir
        </Button>
      )}
      {downLG && !downSM && <MobileSection />}
    </>
  );
}
